import type { HelpPage } from './types'

// English help content. Coverage is partial: high-traffic pages are
// translated; pages that fall through to `getHelpContent`'s fallback
// are served the French version (with a console warning) until a
// translator lands proper EN copy.
//
// Add entries here keyed by route path, mirroring `content.ts`.
export const HELP_CONTENT_EN: Record<string, HelpPage> = {
  '/my': {
    title: 'My space',
    summary: "Your personal dashboard: today's punch, leave balances, and recent payslips.",
    steps: [
      {
        label: 'Punch in',
        detail: 'Click "Punch in" at the start of your day.',
      },
      {
        label: 'Check your balances',
        detail: 'Progress bars show accrued and used leave.',
      },
      {
        label: 'Request leave',
        detail: 'Tap "Request" → choose type, dates, reason.',
      },
      {
        label: 'Punch out',
        detail: 'Same button at end of day to record clock-out.',
      },
    ],
    faq: [
      {
        q: 'My punch is not showing up — what should I do?',
        a: 'Check your internet connection then reload. If it persists, contact HR for a manual adjustment.',
      },
      {
        q: 'I cannot see my payslips',
        a: 'Payslips appear once the payroll period is validated by the payroll team. If a period is missing, contact payroll.',
      },
      {
        q: 'How do I cancel a leave request?',
        a: 'In "My leaves", click the request → "Cancel" button. Only available while still pending.',
      },
    ],
    relatedLinks: [
      { label: 'My leaves', path: '/my/leaves' },
      { label: 'My payslips', path: '/my/payslips' },
      { label: 'My profile', path: '/my/profile' },
    ],
  },

  '/my/leaves': {
    title: 'My leaves',
    summary: 'Check your leave balances and submit requests. Requests follow an approval workflow.',
    steps: [
      { label: 'Pick a leave type', detail: 'Paid leave, sick, family event, etc.' },
      {
        label: 'Select dates',
        detail: 'Weekends and public holidays are automatically excluded from the count.',
      },
      {
        label: 'State the reason',
        detail: 'Required for some types (sick, family event).',
      },
      {
        label: 'Track approval',
        detail: 'Automatic notification to your manager then HR.',
      },
    ],
    fields: [
      {
        name: 'Working days',
        definition:
          'Days counted excluding weekends and public holidays. Mon–Fri = 5 working days.',
      },
      {
        name: 'Available balance',
        definition: 'Accrued days − taken days − pending days.',
      },
      {
        name: 'Half-day',
        definition: 'Tick "half-day start" or "half-day end" for a 0.5-day request.',
      },
    ],
    faq: [
      {
        q: 'Why does my balance not change after approval?',
        a: 'The balance updates on final approval (manager AND HR if required). If both approved and the balance is unchanged, contact HR.',
      },
      {
        q: 'Can I take leave beyond my balance?',
        a: 'No, except unpaid leave. The system blocks requests when the balance is insufficient.',
      },
    ],
    warnings: [
      'An approved request cancelled after the start date must be processed manually by HR.',
    ],
  },

  '/my/settings': {
    title: 'My settings',
    summary:
      'Manage your profile, password, active sessions, and preferences (default workspace, theme, language, timezone).',
    steps: [
      {
        label: 'Profile',
        detail: 'Update your first/last name. Email is read-only.',
      },
      {
        label: 'Security',
        detail:
          'Change your password. Other sessions are signed out automatically. Recent login history is below the form.',
      },
      {
        label: 'Sessions',
        detail: 'List of active devices. Revoke individual sessions or all others at once.',
      },
      {
        label: 'Preferences',
        detail: 'Default workspace on login, light/dark/system theme, FR/EN language, timezone.',
      },
    ],
    faq: [
      {
        q: 'Why am I asked to log in again after changing my password?',
        a: 'For security, all other sessions are revoked when a password is changed.',
      },
    ],
  },

  '/dashboard': {
    title: 'Dashboard',
    summary:
      'High-level overview of activity: quotes, bookings, shipments, recent invoices, and KPIs.',
    relatedLinks: [
      { label: 'Quotes', path: '/quotes' },
      { label: 'Shipments', path: '/shipments' },
      { label: 'Invoices', path: '/finance/invoices' },
    ],
  },

  '/quotes': {
    title: 'Quotes',
    summary:
      'Generate freight quotes for customers (sea, air, road, multimodal). Quotes can be sent and accepted online.',
    steps: [
      { label: 'New quote', detail: 'Pick a customer, mode, lanes, weights/volumes.' },
      {
        label: 'Apply tariffs',
        detail: 'The system suggests tariffs by O/D and mode. You can override.',
      },
      { label: 'Send', detail: 'Email the quote with a public accept link.' },
      {
        label: 'Convert',
        detail: 'Once accepted, the quote becomes a booking automatically.',
      },
    ],
  },

  '/shipments': {
    title: 'Shipments',
    summary:
      'Track bookings end-to-end: pickup, transit, customs clearance, delivery. Live map and per-leg status.',
    relatedLinks: [
      { label: 'Customs', path: '/customs' },
      { label: 'Warehouse', path: '/warehouse' },
    ],
  },

  '/finance/invoices': {
    title: 'Invoices',
    summary:
      'Issue, send, and reconcile customer invoices. Automatic VAT (Senegal 18% by default) and SYSCOHADA accounting integration.',
    steps: [
      { label: 'Create', detail: 'Pick a customer and shipment(s).' },
      { label: 'Send', detail: 'Email PDF with payment instructions.' },
      { label: 'Reconcile', detail: 'Match incoming payments to AR balances.' },
    ],
    fields: [
      {
        name: 'XOF',
        definition: 'CFA Franc — primary currency. Secondary display in USD/EUR is informational.',
      },
      { name: 'VAT', definition: 'Senegal default 18%, configurable per tenant.' },
    ],
  },

  '/admin/users': {
    title: 'Users',
    summary:
      'Tenant-wide user directory: invite, activate/deactivate, unlock, reset password, revoke sessions.',
    steps: [
      {
        label: 'Invite',
        detail: 'Header button. Enter email and a role. The invitee receives an email link.',
      },
      {
        label: 'Manage roles',
        detail:
          'Open a user → Roles tab → grant/revoke. The "general_manager" role grants the wildcard `*`.',
      },
      {
        label: 'Lockout / reset',
        detail:
          'A user locked after too many failed logins can be unlocked. Resetting a password returns a one-time temporary password.',
      },
    ],
  },

  '/admin/roles': {
    title: 'Roles',
    summary:
      'Define custom roles with grouped permissions and wildcards. System roles are read-only.',
    steps: [
      {
        label: 'New custom role',
        detail: 'Header button creates an empty role; you then enable permissions.',
      },
      {
        label: 'Group permissions',
        detail:
          'Each resource (invoices, shipments, …) can be set granularly or via the resource wildcard `<resource>:*`.',
      },
      {
        label: 'Global wildcard',
        detail: 'Toggle `*` at the top to grant every resource (admin only).',
      },
    ],
    fields: [
      {
        name: 'Wildcard',
        definition:
          '`<resource>:*` covers all actions of one resource. `*:read` covers reads of all resources. `*` covers everything.',
      },
      {
        name: 'Priority',
        definition:
          'When two roles conflict, the higher-priority role wins (used for delegation overrides).',
      },
    ],
  },

  '/admin/audit': {
    title: 'Audit log',
    summary:
      'Append-only compliance log. Search by resource, actor, action; expand a row to see before/after diffs.',
  },

  '/admin/delegations': {
    title: 'Delegations',
    summary:
      'Temporary role transfer between users (vacation cover, etc.). States: pending → active → expired/revoked.',
    fields: [
      {
        name: 'Delegator',
        definition:
          'The user transferring their roles. Optional for admin (admin can act on behalf).',
      },
      {
        name: 'Delegate',
        definition: 'The user receiving the temporary roles.',
      },
      {
        name: 'Period',
        definition:
          'A cron picks up `pending` delegations and activates them on `start_at`, expires them on `end_at`.',
      },
    ],
  },

  // ============================================================
  // CRM (M08)
  // ============================================================

  '/crm': {
    title: 'Customer CRM',
    summary:
      'Commercial overview: Gold/Silver/Bronze segmentation, credit scoring, churn-risk customers, and follow-up actions.',
    faq: [
      {
        q: 'How is the credit score computed?',
        a: '0-100 score weighted across 3 metrics: average days to pay (40%), overdue invoice count (35%), tenure (25%). Weights are tunable in CRM → Settings.',
      },
      {
        q: 'When are segments recomputed?',
        a: 'Daily at 06:00 UTC for the current period, and on every received payment or delivered shipment.',
      },
    ],
  },

  '/crm/customers': {
    title: 'Customers (CRM view)',
    summary:
      'Enriched list with segmentation, credit score, and churn indicator. Quick filters by tier and risk level.',
    fields: [
      {
        name: 'Tier (Gold / Silver / Bronze)',
        definition:
          'Segment derived from trailing-twelve-month revenue. Thresholds are configurable in CRM → Settings.',
      },
      {
        name: 'Credit score',
        definition:
          'Financial reliability index from 0 (risky) to 100 (excellent). Combines payment days, overdue invoices, and tenure.',
      },
      {
        name: 'Churn risk',
        definition:
          'Low = active customer, Medium = drifting away, High = urgent action required (commercial follow-up, gesture).',
      },
    ],
  },

  '/crm/churn-risk': {
    title: 'At-risk customers',
    summary:
      'Filtered list of customers in high churn risk. Open the detail page to see the interaction history and trigger a follow-up.',
  },

  '/crm/settings': {
    title: 'CRM settings',
    summary:
      'Segmentation thresholds, churn windows, credit-score weights. Any change applies to the next scoring run.',
    warnings: [
      'The three weights must sum to 100.',
      'Gold threshold must be strictly greater than the Silver threshold.',
    ],
  },

  // ============================================================
  // SUPPLIERS — Master data + AP cycle (M10)
  // ============================================================

  '/suppliers': {
    title: 'Suppliers',
    summary:
      "AMA's supplier registry: carriers, customs brokers, insurers, garages, landlords… Each supplier goes through a qualification cycle before becoming eligible to receive vendor bills.",
    steps: [
      {
        label: 'Create a supplier',
        detail:
          '"New supplier" button. Fill in legal name (required), NINEA (14 digits), category, contact and payment terms. The supplier starts in "in qualification" status.',
      },
      {
        label: 'Qualify',
        detail:
          'Open the detail page → "Qualify" button. The supplier transitions to "qualified" and becomes selectable when entering a vendor bill.',
      },
      {
        label: 'Suspend / Reactivate',
        detail:
          'Suspending blocks new bills without archiving the supplier. Reactivation is immediate. A reason is mandatory when suspending.',
      },
      {
        label: 'Blacklist',
        detail:
          'Terminal action: the supplier can no longer be reused. Reserved for serious cases (dispute, fraud). Reason mandatory, kept for audit.',
      },
    ],
    fields: [
      {
        name: 'NINEA',
        definition: '14 digits. Senegalese tax identifier. Strict validation server-side.',
      },
      {
        name: 'Category',
        definition:
          'carrier, customs_broker, insurer, fuel, maintenance, it_services, professional, utilities, rent, security, other. Used for AP reporting and accounting routing.',
      },
      {
        name: 'Qualification status',
        definition:
          'in_qualification → qualified → suspended → blacklisted. Only "qualified" suppliers can receive new bills.',
      },
      {
        name: 'Payment terms',
        definition:
          'Default term copied onto bills issued by this supplier (0/15/30/45/60/90 days).',
      },
      {
        name: 'Average rating',
        definition: 'Auto-recomputed on every new periodic evaluation (0 to 5).',
      },
    ],
    warnings: [
      'A vendor bill submitted with an unqualified supplier is rejected by the API (409 SUPPLIER_NOT_QUALIFIED).',
      'Blacklisting is terminal: no transition back is possible.',
    ],
    relatedLinks: [
      { label: 'Vendor bills', path: '/finance/vendor-bills' },
      { label: 'Vendor payments', path: '/finance/vendor-payments' },
    ],
    faq: [
      {
        q: "My supplier isn't showing in the bill picker — why?",
        a: 'The picker filters to qualified suppliers by default. Check the status on the detail page and qualify if needed.',
      },
      {
        q: 'How do I record bank details?',
        a: 'Edit the supplier → "Bank details" section. IBAN, SWIFT, mobile money (Wave / OM) are supported. The account number is used when recording a vendor payment.',
      },
      {
        q: 'What is "Supplier label (legacy)" on bills?',
        a: 'A free-text field kept for backwards compatibility (bills entered before M10). New bills use supplier_id from the registry.',
      },
    ],
  },

  // ============================================================
  // MY space — additions
  // ============================================================

  '/my/payslips': {
    title: 'My payslips',
    summary:
      'View and download your monthly payslips. Payslips become available once the period is validated.',
    faq: [
      {
        q: 'How do I read my payslip?',
        a: '"Earnings" section: gross salary and bonuses. "Contributions" section: deductions (IPRES, IRPP). "Net to pay" is what is wired to your account.',
      },
      {
        q: 'What is IPRES?',
        a: "Senegal's pension fund (Institut de Prévoyance Retraite du Sénégal). 5.6% withheld on capped gross salary.",
      },
      {
        q: 'What is IRPP?',
        a: 'Personal income tax. Computed on a progressive scale, with allowances for dependents.',
      },
      {
        q: 'Found an error on my payslip — what do I do?',
        a: 'Contact payroll right away. Corrections are issued on the next period through an adjustment line.',
      },
    ],
  },

  '/my/profile': {
    title: 'My profile',
    summary: 'Manage personal info, change your password, and configure display preferences.',
    steps: [
      {
        label: 'Edit your profile',
        detail: 'Name, email, phone. The email is used for notifications.',
      },
      {
        label: 'Change password',
        detail: 'Minimum 10 characters with upper-case, lower-case and a digit.',
      },
      {
        label: 'Pick your language',
        detail: 'French or English. Affects the UI and notifications.',
      },
    ],
    warnings: ['Changing your password may automatically sign out your other active sessions.'],
  },

  '/my/attendance': {
    title: 'My attendance',
    summary: 'History of your clock-in/clock-out punches. Lets you punch from the web UI.',
    faq: [
      {
        q: 'I forgot to clock out yesterday',
        a: 'Contact your HR manager for a manual adjustment. The reason will be logged in the audit trail.',
      },
    ],
  },

  '/my/leaves/new': {
    title: 'Request leave',
    summary: 'Submit a leave request. Available balance is shown in real time.',
    steps: [
      { label: 'Leave type', detail: 'Paid, sick, family event, unpaid.' },
      {
        label: 'Dates',
        detail: 'Working days are auto-computed (excludes weekends and public holidays).',
      },
      { label: 'Reason', detail: 'Required for some leave types.' },
      { label: 'Submit', detail: 'Manager is notified. Status: "Pending".' },
    ],
  },

  '/my/documents': {
    title: 'My documents',
    summary: 'Your personal documents: contract, attestations, certificates. Downloadable as PDF.',
  },

  // ============================================================
  // SETTINGS
  // ============================================================

  '/settings': {
    title: 'Settings',
    summary:
      'Org-wide configuration: legal info, accounting parameters, HR, chart of accounts, tariffs, and users.',
    relatedLinks: [
      { label: 'Organization', path: '/settings/organization' },
      { label: 'Users', path: '/settings/users' },
      { label: 'Preferences', path: '/settings/preferences' },
      { label: 'Chart of accounts', path: '/settings/chart-of-accounts' },
    ],
  },

  '/settings/users': {
    title: 'Users',
    summary:
      'Manage your organization’s user accounts: invite, activate, deactivate, assign roles.',
    steps: [
      {
        label: 'Invite a user',
        detail: '"Invite" button → email + roles → link valid for 72 hours.',
      },
      {
        label: 'Assign roles',
        detail: 'User profile → "Roles" tab → Assign.',
      },
      {
        label: 'Deactivate an account',
        detail: 'Actions → Deactivate. The account can no longer sign in but data is preserved.',
      },
      {
        label: 'Unlock an account',
        detail: 'If a user has been locked after 5 failed attempts, use "Unlock".',
      },
    ],
    fields: [
      {
        name: 'Role',
        definition: 'Drives application permissions. A user can hold multiple cumulative roles.',
      },
      {
        name: 'Active flag',
        definition: 'Active = can sign in. Inactive = sign-in blocked but data kept.',
      },
      {
        name: 'Last login',
        definition: 'Date and IP of the last successful sign-in.',
      },
    ],
    faq: [
      {
        q: "Invitation didn't arrive?",
        a: 'Check spam. Use "Resend invitation" on the user profile. The link expires after 72 hours.',
      },
      {
        q: 'Can a user be permanently deleted?',
        a: 'No. Accounts are deactivated, never deleted (operational traceability). Contact support for a GDPR removal.',
      },
    ],
    warnings: ['A user must always hold at least one role. Removing the last role is blocked.'],
    relatedLinks: [
      { label: 'Preferences', path: '/settings/preferences' },
      { label: 'Organization', path: '/settings/organization' },
    ],
  },

  '/settings/organization': {
    title: 'Organization',
    summary:
      'Legal info for your organization: name, NINEA, address, RC. These appear on every generated document.',
    faq: [
      {
        q: 'How do I update the org name and NINEA?',
        a: 'Type the new values and save. They appear on invoices, payslips, and tax filings.',
      },
    ],
    warnings: [
      'Changes to legal parameters (NINEA, address) impact every document generated from the change date onward.',
    ],
  },

  '/settings/preferences': {
    title: 'My preferences',
    summary: 'Configure your interface: language, time zone, date format, display currency.',
  },

  '/settings/profile': {
    title: 'My profile (settings)',
    summary: 'Personal info: name, email, phone, photo. The email is used for notifications.',
  },

  '/settings/security': {
    title: 'Security',
    summary:
      'Password, active sessions, two-factor authentication (2FA). Strongly recommended for admin accounts.',
    warnings: ['Enabling 2FA is strongly recommended for accounts with access to financial data.'],
  },

  '/settings/accounting': {
    title: 'Accounting parameters',
    summary:
      'Fiscal year, accounting journals, VAT settings, default accounts for auto-generated entries.',
  },

  '/settings/chart-of-accounts': {
    title: 'Chart of accounts',
    summary:
      'SYSCOHADA chart of accounts. Pre-populated by industry. Customizable (analytic sub-accounts).',
    warnings: ['Editing or deleting an account that already has entries is blocked.'],
  },

  '/settings/hr': {
    title: 'HR settings',
    summary:
      'HR configuration: leave types, annual balances, public holidays, applicable collective agreements.',
  },

  '/settings/email': {
    title: 'Email settings',
    summary: 'SMTP configuration for sending notifications, invoices and payslips by email.',
  },

  '/settings/documents': {
    title: 'Document templates',
    summary:
      'Customize templates: invoices, quotes, payslips, letters. Logo, legal mentions, signatures.',
  },

  '/settings/tariffs': {
    title: 'Tariffs',
    summary: 'Pricing grids used in quotes: per kg, per m³, per destination, etc.',
  },

  // ============================================================
  // HR
  // ============================================================

  '/hr': {
    title: 'HR dashboard',
    summary:
      'HR overview: active headcount, ongoing leaves, pending requests, contracts ending soon.',
    faq: [
      {
        q: 'How do I create a first employee?',
        a: '"Employees" menu → "New employee". Enter identity + hire date. Then create a contract from the employee profile.',
      },
      {
        q: 'What does "contracts ending in 30d" mean?',
        a: 'Fixed-term or internship contracts whose end date falls within 30 days. Action required: renew or close.',
      },
    ],
    relatedLinks: [
      { label: 'Employee list', path: '/hr/employees' },
      { label: 'Leaves', path: '/hr/leaves' },
      { label: 'Calendar', path: '/hr/leaves/calendar' },
    ],
  },

  '/hr/employees': {
    title: 'Employees',
    summary:
      'Full list of active and archived employees. Each profile bundles identity, contract, leaves and attendance.',
    steps: [
      {
        label: 'Create an employee',
        detail: '"New employee" → identity, department, position, hire date.',
      },
      {
        label: 'Create their contract',
        detail: 'From the profile → "Contract" tab → "New contract".',
      },
      {
        label: 'Link a user account',
        detail:
          'If the employee has an AMANET account, link it on the profile to enable self-service.',
      },
    ],
    fields: [
      {
        name: 'Employee code',
        definition: 'Auto-generated unique id (EMP-0001). Used on payslips and statutory filings.',
      },
      {
        name: 'IPRES',
        definition: 'IPRES (pension) registration number. Required for monthly filings.',
      },
      {
        name: 'CSS',
        definition:
          'Senegalese Social Security registration number. Required for AT/family benefits filings.',
      },
      {
        name: 'Dependents',
        definition: 'Number of children and dependents. Used to compute the IRPP allowance.',
      },
    ],
    faq: [
      {
        q: 'Can an employee hold two active contracts?',
        a: 'No. Only one active contract per employee. To switch, terminate the previous one and create the new one.',
      },
      {
        q: 'How do I record a resignation or a dismissal?',
        a: 'Employee profile → Actions → "Terminate contract". Provide reason and effective date.',
      },
    ],
    warnings: ['Employees cannot be deleted. Use "Status: Terminated" to archive.'],
  },

  '/hr/employees/new': {
    title: 'New employee',
    summary:
      'Create an employee record. At minimum provide identity and hire date. The contract is created from the profile.',
    steps: [
      {
        label: 'Required fields',
        detail: 'First name, last name, hire date. The rest can be added later.',
      },
      {
        label: 'Statutory ids',
        detail: 'IPRES and CSS numbers — required for payroll.',
      },
      {
        label: 'Dependents',
        detail: 'Directly impacts IRPP computation.',
      },
      {
        label: 'Link a user account',
        detail: 'Optional — enables self-service (leaves, payslips).',
      },
    ],
    fields: [
      {
        name: 'Hire date',
        definition: 'Drives seniority and pro-rated leave for the first year.',
      },
      {
        name: 'Marital status',
        definition: 'Single = 1 tax part. Married = 2 parts. Impacts IRPP.',
      },
    ],
  },

  '/hr/contracts': {
    title: 'Contracts',
    summary:
      'Manage employment contracts. Available types: CDI, CDD, internship, apprenticeship, agency.',
    fields: [
      {
        name: 'CDI',
        definition: 'Open-ended contract. No end date. Terminated by resignation or dismissal.',
      },
      {
        name: 'CDD',
        definition:
          'Fixed-term contract. Maximum 2 renewals in Senegal. Beyond that, auto-converts to CDI.',
      },
      {
        name: 'Probation period',
        definition:
          'Window during which either party may terminate without notice. Length varies by collective agreement.',
      },
      {
        name: 'Base salary',
        definition: 'Monthly gross used to compute IPRES, CSS and IRPP contributions.',
      },
      {
        name: 'Amendment',
        definition:
          'Edit of an existing contract (raise, position change). Tracks before/after values.',
      },
    ],
    faq: [
      {
        q: 'How do I record a salary raise?',
        a: 'Contract page → "Create amendment" → type "Salary change". Amendment history is preserved.',
      },
    ],
    warnings: [
      'A validated contract is immutable. All changes go through a timestamped amendment.',
    ],
  },

  '/hr/contracts/new': {
    title: 'New contract',
    summary:
      'Create an employment contract linked to an existing employee. Defines salary, position and terms.',
    fields: [
      { name: 'Start date', definition: 'First effective day. Drives seniority.' },
      {
        name: 'Probation period',
        definition: '1 to 3 months by category. Renewable once.',
      },
      {
        name: 'Base salary',
        definition: 'Monthly gross. Base for social contributions.',
      },
    ],
  },

  '/hr/leaves': {
    title: 'Leaves — Admin',
    summary:
      'Manage every employee leave request. Approve, reject, and review the absence calendar.',
    steps: [
      {
        label: 'See pending requests',
        detail: 'Filter by "Pending manager" or "Pending HR".',
      },
      {
        label: 'Approve a request',
        detail: 'Open the request → "Approve". Comment is optional.',
      },
      {
        label: 'Reject with a reason',
        detail: '"Reject" → enter the reason. The employee is notified and balance restored.',
      },
      {
        label: 'Create a request for an employee',
        detail: '"New request" → pick the employee.',
      },
    ],
    faq: [
      {
        q: 'When is the balance debited?',
        a: 'The balance is "reserved" upon submission (pending). It is permanently debited on final approval.',
      },
      {
        q: 'How does two-tier approval work?',
        a: 'Some types (sick, maternity) require manager AND HR approval. Others (family events) go straight to HR.',
      },
    ],
    relatedLinks: [{ label: 'Leave calendar', path: '/hr/leaves/calendar' }],
  },

  '/hr/leaves/calendar': {
    title: 'Leave calendar',
    summary:
      'Calendar view of all employee absences. Helps anticipate critical periods (low staff).',
  },

  '/hr/leaves/new': {
    title: 'New leave request',
    summary:
      'Create a request for an employee (admin side). For employee self-service, see "My leaves".',
  },

  '/hr/analytics': {
    title: 'HR analytics',
    summary: 'HR KPIs: headcount, turnover, average tenure, absenteeism rate, age pyramid.',
  },

  '/hr/documents': {
    title: 'HR documents',
    summary:
      'Library of HR documents: internal policy, agreements, procedures. Visible to all employees per ACL.',
  },

  // ============================================================
  // ACCOUNTING (M09 SYSCOHADA)
  // ============================================================

  '/accounting': {
    title: 'Accounting',
    summary:
      'Accounting dashboard: month result, cash, open receivables/payables, and quick links to SYSCOHADA financial statements.',
    relatedLinks: [
      { label: 'Trial balance', path: '/accounting/trial-balance' },
      { label: 'Income statement', path: '/accounting/income-statement' },
      { label: 'Balance sheet', path: '/accounting/balance-sheet' },
      { label: 'Journal entries', path: '/accounting/entries' },
      { label: 'Bank reconciliation', path: '/accounting/reconciliation' },
      { label: 'VAT', path: '/accounting/vat' },
    ],
  },

  '/accounting/trial-balance': {
    title: 'Trial balance',
    summary:
      'Recap of every account with cumulative debit, credit and balance at a date. SYSCOHADA-compliant.',
    fields: [
      {
        name: 'Cumulative debit',
        definition: 'Total debit movements on the account since fiscal year opening.',
      },
      {
        name: 'Cumulative credit',
        definition: 'Total credit movements on the account since fiscal year opening.',
      },
      {
        name: 'Balance',
        definition:
          'Debit − Credit for debit-balance accounts (assets/expenses). Credit − Debit for credit-balance accounts (liabilities/income).',
      },
      {
        name: 'Balanced trial balance',
        definition:
          'Total debit must ALWAYS equal total credit. The fundamental double-entry rule.',
      },
    ],
    faq: [
      {
        q: 'What does the "imbalance" line at the bottom mean?',
        a: 'Critical error. A journal entry was created without respecting double-entry. Contact IMPACT SOLUTIONS.',
      },
      {
        q: 'How do I filter by account class?',
        a: 'Click the "Class 1" through "Class 8" buttons above the table.',
      },
      {
        q: 'What happens when I click an account?',
        a: 'Opens the general ledger for that account: every movement with a running balance.',
      },
    ],
    relatedLinks: [
      { label: 'Income statement', path: '/accounting/income-statement' },
      { label: 'Balance sheet', path: '/accounting/balance-sheet' },
    ],
  },

  '/accounting/income-statement': {
    title: 'Income statement',
    summary:
      'Financial statement comparing income (class 7) and expenses (class 6) over a period. The result is the profit or loss.',
    fields: [
      {
        name: 'Income (class 7)',
        definition: 'Revenue from operations: freight services (706000), other income (75x, 77x).',
      },
      {
        name: 'Expenses (class 6)',
        definition:
          'Operating costs: salaries (64x), subcontracting (611000), fuel (624000), depreciation (68x).',
      },
      {
        name: 'Net result',
        definition:
          'Income − Expenses. Positive = profit. Negative = loss. Carried to the balance sheet at account 130000.',
      },
    ],
    faq: [
      {
        q: 'Do unpaid invoices appear in the result?',
        a: 'Yes. Accrual accounting books revenue at invoice date, regardless of payment.',
      },
    ],
    warnings: ['Figures only reflect VALIDATED entries. Drafts are excluded.'],
  },

  '/accounting/balance-sheet': {
    title: 'Balance sheet',
    summary:
      "Snapshot of the company's assets at a given date: assets (what you own) vs. liabilities (what you owe).",
    fields: [
      {
        name: 'Assets',
        definition: 'Classes 2 (fixed assets), 3 (inventory), 4-debit (receivables), 5 (cash).',
      },
      {
        name: 'Liabilities',
        definition: 'Classes 1 (equity), 4-credit (payables, social, tax).',
      },
      {
        name: 'Balanced',
        definition:
          'Total assets = Total liabilities + Equity. An accounting identity. Imbalance signals an error.',
      },
    ],
    faq: [
      {
        q: 'How do customer invoices impact the balance sheet?',
        a: 'Issued invoices increase 411000 (Customers) on the asset side. A received payment reduces 411 and increases 521 (Bank).',
      },
    ],
  },

  '/accounting/entries': {
    title: 'Journal entries',
    summary:
      'All accounting entries: automatic (from invoices and payroll) and manual. Grouped by journal.',
    fields: [
      {
        name: 'VT journal',
        definition: 'Sales journal. Auto-generated from customer invoices (M03).',
      },
      {
        name: 'BQ journal',
        definition: 'Bank journal. Settlement entries (payment received from a customer).',
      },
      {
        name: 'PA journal',
        definition: 'Payroll journal. Monthly entries created on payroll period validation.',
      },
      {
        name: 'OD journal',
        definition: 'Miscellaneous Operations. For manual adjustment entries.',
      },
      {
        name: 'Validated entry',
        definition: 'Final, immutable. CANNOT be edited. Correct via reversal.',
      },
      {
        name: 'Reversal',
        definition:
          'Mirror entry of a validated one. Cancels the accounting effect without erasing the audit trail.',
      },
    ],
    faq: [
      {
        q: 'Why can I not edit a validated entry?',
        a: 'It\'s a SYSCOHADA rule (immutability). To correct, use "Reverse" then create a new correct entry.',
      },
    ],
    warnings: [
      'A manual entry must always balance (debit = credit) before validation. The system blocks unbalanced entries.',
    ],
  },

  '/accounting/entries/new': {
    title: 'New manual entry',
    summary:
      'Create a manual accounting entry (OD). Use for adjustments, provisions, or corrections.',
    steps: [
      {
        label: 'Pick the journal',
        detail: 'OD for miscellaneous, BQ if bank-related.',
      },
      {
        label: 'Enter the description',
        detail: 'Clear text required (e.g. "Q2 2026 leave provision").',
      },
      {
        label: 'Add lines',
        detail: 'At least 2 lines. One account per line. Type the code or search by name.',
      },
      {
        label: 'Verify the balance',
        detail:
          'Total debit = Total credit required. The "Validate" button stays disabled while unbalanced.',
      },
    ],
    fields: [
      {
        name: 'Account (code)',
        definition:
          '6-digit SYSCOHADA code. E.g. 411000 = Customers, 521000 = Bank, 706000 = Freight services.',
      },
      {
        name: 'Debit vs Credit',
        definition:
          'Debit = increase on an asset/expense. Credit = increase on a liability/income. A line can never have both.',
      },
    ],
    warnings: [
      'Entries are validated and immutable. Double-check before validating.',
      'Never use a manual entry for operations already auto-generated (invoices, payroll). Risk of duplicates.',
    ],
    relatedLinks: [{ label: 'Chart of accounts', path: '/settings/chart-of-accounts' }],
  },

  '/accounting/reconciliation': {
    title: 'Bank reconciliation',
    summary:
      'Reconcile accounting entries with bank statements. Ensures account 521 reflects actual cash.',
    steps: [
      {
        label: 'Import the statement',
        detail: 'CSV or OFX format provided by your bank.',
      },
      {
        label: 'Run auto-matching',
        detail:
          'The system pairs statement lines with accounting entries by amount, date, and reference.',
      },
      {
        label: 'Handle discrepancies',
        detail:
          'Unmatched lines: create the missing entry, mark as bank fees, or open an investigation.',
      },
      {
        label: 'Validate the reconciliation',
        detail: 'Once 100% of lines are processed, validate to close the period.',
      },
    ],
    fields: [
      {
        name: 'Theoretical balance',
        definition: 'Account 521 balance in the trial balance at the statement date.',
      },
      {
        name: 'Bank balance',
        definition: 'Balance as shown by the bank on the statement.',
      },
      {
        name: 'Variance',
        definition:
          'Difference between theoretical and bank balance. Must be explained by in-flight items (issued checks not yet debited, transfers in transit).',
      },
    ],
    faq: [
      {
        q: 'How do I import a statement?',
        a: '"New reconciliation" button → pick the bank account → upload the CSV/OFX file.',
      },
      {
        q: 'A statement line is not recognized',
        a: 'Likely a bank-only operation (fees) without an accounting entry. Create the missing entry from the unmatched line.',
      },
    ],
    warnings: ['A validated reconciliation is locked. To edit, unlock the related period.'],
  },

  '/accounting/vat': {
    title: 'VAT filings',
    summary:
      'Monthly VAT filings to DGID. Auto-computes collected VAT (sales) minus deductible VAT (purchases).',
    fields: [
      {
        name: 'Collected VAT',
        definition: 'VAT charged to customers (account 445810). Owed to the State.',
      },
      {
        name: 'Deductible VAT',
        definition: 'VAT paid to suppliers (account 445660). Recoverable against collected VAT.',
      },
      {
        name: 'VAT due',
        definition:
          'Collected − Deductible. Positive: pay to the State. Negative: carryforward credit.',
      },
      {
        name: 'VAT credit',
        definition:
          'Deductible > Collected. Carried over to the next filing or refund request (under conditions).',
      },
    ],
    faq: [
      {
        q: 'When is the VAT filing due?',
        a: 'No later than the 15th of the following month. Senegal standard rate is 18% (account 445810).',
      },
    ],
    warnings: ['A validated filing is frozen. Any correction goes through a rectifying filing.'],
  },

  '/accounting/accounts': {
    title: 'General ledger — Account',
    summary:
      'Account-level activity: every entry with a running balance. Helps trace the source of each movement.',
    fields: [
      {
        name: 'Running balance',
        definition: 'Account balance after each entry (cumulative since opening).',
      },
    ],
    faq: [
      {
        q: 'How do I see the source entry?',
        a: 'Click the line → opens the full entry (all counterparts).',
      },
    ],
  },

  // ============================================================
  // FINANCE — additions
  // ============================================================

  '/finance': {
    title: 'Finance dashboard',
    summary: 'Quick view: month collections, open receivables, overdue invoices, cash KPIs.',
    faq: [
      {
        q: 'What does "AR" mean?',
        a: 'Accounts Receivable — invoiced but not yet collected.',
      },
      {
        q: 'How do I find overdue invoices?',
        a: '"Overdue" section on the dashboard, or invoice list filtered by "Overdue" status.',
      },
    ],
    relatedLinks: [
      { label: 'Invoices', path: '/finance/invoices' },
      { label: 'AR', path: '/finance/ar' },
      { label: 'Trial balance', path: '/accounting/trial-balance' },
    ],
  },

  '/finance/ar': {
    title: 'Accounts receivable (AR)',
    summary: 'Track unpaid invoices and aging. Helps target collection actions.',
    fields: [
      {
        name: 'Aging balance',
        definition: 'Receivables split by bucket: 0-30d, 31-60d, 61-90d, 90d+.',
      },
      {
        name: 'DSO',
        definition: 'Days Sales Outstanding — average days to collect. Key cash KPI.',
      },
    ],
    faq: [
      {
        q: 'How do I trigger a dunning?',
        a: 'Select the invoices → "Send reminder". Auto email to the customer with the invoice PDF.',
      },
    ],
  },

  '/finance/payments/new': {
    title: 'New payment',
    summary:
      'Record a customer collection. Each payment is allocated to one or more invoices and creates a bank entry.',
    steps: [
      {
        label: 'Enter amount and method',
        detail: 'Received amount, payment method, date, reference.',
      },
      {
        label: 'Allocate to invoices',
        detail: 'Pick the invoice(s) covered by this payment.',
      },
      {
        label: 'Verify the entry',
        detail: 'DR 521000 (Bank) / CR 411000 (Customers) auto-generated.',
      },
    ],
    fields: [
      {
        name: 'Wire reference',
        definition: 'Bank wire or check reference. For matching against the bank statement.',
      },
      {
        name: 'Allocation',
        definition:
          'Link between a payment and one or more invoices. A single payment can settle several invoices.',
      },
    ],
  },

  '/finance/vendor-bills': {
    title: 'Vendor bills',
    summary:
      'Capture and validate bills received from suppliers. Posts the accounting commitment and feeds the payment schedule.',
    steps: [
      {
        label: 'Enter the bill',
        detail: 'Supplier, number, date, amounts (net/VAT/gross), due date.',
      },
      {
        label: 'Attach the PDF',
        detail: 'Upload the original document (required for archival).',
      },
      {
        label: 'Validate',
        detail: 'Posts the entry (DR 60x/61x... / CR 401 / DR 445660 VAT).',
      },
      {
        label: 'Schedule the payment',
        detail: 'On the due date, create a vendor payment.',
      },
    ],
    fields: [
      {
        name: '3-way match',
        definition: 'Cross-check between Purchase Order / Goods Receipt / Bill before validation.',
      },
    ],
    warnings: [
      'A validated bill creates a debt at account 401. Double-check amounts BEFORE validating.',
    ],
  },

  // ============================================================
  // PAYROLL (M12)
  // ============================================================

  '/payroll': {
    title: 'Payroll dashboard',
    summary: 'Overview of payroll periods. One period per month: compute, validate, pay.',
    faq: [
      {
        q: 'In what order do I run a payroll period?',
        a: 'Create → Compute → Verify → Validate → Pay → Close. Each step is irreversible.',
      },
    ],
    relatedLinks: [{ label: 'Payroll periods', path: '/payroll/periods' }],
  },

  '/payroll/periods': {
    title: 'Payroll periods',
    summary:
      'Manage the monthly payroll cycle. Each period covers one month and produces one payslip per active employee.',
    steps: [
      {
        label: 'Create the period',
        detail: '"New period" → pick month/year. Only one period allowed per month.',
      },
      {
        label: 'Compute payslips',
        detail:
          '"Compute" — the system auto-computes IPRES, CSS, IRPP, Trimf for each active employee.',
      },
      {
        label: 'Verify payslips',
        detail: 'Review each generated payslip. On error: recompute (allowed before validation).',
      },
      {
        label: 'Validate and post',
        detail: '"Validate" — posts the accounting entry (PA journal) in M09. Irreversible.',
      },
      {
        label: 'Mark as paid',
        detail: 'After wiring salaries to employee accounts.',
      },
      {
        label: 'Close',
        detail: 'Permanently locks the period. No further changes possible.',
      },
    ],
    fields: [
      {
        name: 'Total gross salaries',
        definition: 'Sum of all gross salaries + bonuses. Base for employer contributions.',
      },
      {
        name: 'Total net',
        definition: 'Sum of net-to-pay amounts. Total to wire to employees.',
      },
      {
        name: 'Total employer cost',
        definition: 'Gross + employer contributions (IPRES + CSS). Real labor cost.',
      },
    ],
    faq: [
      {
        q: 'What if an employee was created mid-month?',
        a: 'The compute is auto-prorated based on hire date. Review the individual payslip.',
      },
      {
        q: 'How do I correct an error after validation?',
        a: 'Cancel the affected payslip → create an adjustment payslip on the next period with a difference line.',
      },
    ],
    warnings: [
      'Period validation is IRREVERSIBLE. It posts a permanent accounting entry to the PA journal.',
      'Make sure all contracts are up to date BEFORE computing the period.',
    ],
  },

  '/payroll/analytics': {
    title: 'Payroll analytics',
    summary: 'Analytics on total payroll cost, social contributions and their evolution over time.',
    fields: [
      {
        name: 'Gross payroll evolution',
        definition: 'Variation of total gross salaries month over month. Key HR KPI.',
      },
      {
        name: 'Employer contributions',
        definition: 'IPRES + CSS + employer AT. Depends on gross payroll.',
      },
    ],
  },

  '/payroll/declarations': {
    title: 'Statutory filings',
    summary: 'Monthly/quarterly IPRES, CSS, IRPP filings to social and tax authorities.',
    fields: [
      {
        name: 'IPRES filing',
        definition:
          'Monthly statement detailing the contribution base and pension contributions per employee.',
      },
      {
        name: 'CSS filing',
        definition: 'Monthly family-benefits and work-accident contributions statement.',
      },
      {
        name: 'IRPP filing',
        definition: 'Monthly remittance of withholding income tax.',
      },
    ],
    warnings: ['Once filed, statements can only be corrected by a rectifying filing.'],
  },

  // ============================================================
  // BOOKINGS / QUOTES (additions)
  // ============================================================

  '/bookings': {
    title: 'Bookings',
    summary:
      'List of active bookings. A booking is created on quote acceptance and triggers the shipment cycle.',
    fields: [
      {
        name: 'Booking reference',
        definition: 'Format BK-YYYY-MM-XXXXX. Unique id sent to the customer.',
      },
    ],
    relatedLinks: [
      { label: 'Quotes', path: '/quotes' },
      { label: 'Shipments', path: '/shipments' },
    ],
  },

  '/quotes/new': {
    title: 'New quote',
    summary: 'Create a quote: customer, service type, goods, amounts, validity.',
    steps: [
      { label: 'Pick the customer', detail: 'Existing or create on the fly.' },
      {
        label: 'Describe the service',
        detail: 'Origin, destination, goods type, incoterm.',
      },
      {
        label: 'Enter price lines',
        detail: 'Freight, customs, handling, etc. One line per VAT bracket.',
      },
      { label: 'Set validity', detail: 'Acceptance deadline (default 30 days).' },
    ],
  },

  '/shipments/map': {
    title: 'Shipments map',
    summary: 'Real-time map of vehicle positions and ongoing shipments.',
    faq: [
      {
        q: 'A vehicle position is stale',
        a: 'Tracking depends on the driver app (Capacitor). Check the driver is signed in and has granted geolocation.',
      },
    ],
  },

  // ============================================================
  // CUSTOMS
  // ============================================================

  '/customs': {
    title: 'Customs clearance',
    summary:
      'From the file to the detail note: currency and rate, amounts, articles, liquidation, then the PDF.',
    steps: [
      {
        label: 'Create the file',
        detail: 'Choose the FOB currency and the rate toward XOF. For XOF, the rate is 1.',
      },
      {
        label: 'Enter the amounts',
        detail: 'FOB, freight and insurance in the origin currency. XOF is calculated.',
      },
      { label: 'Add articles', detail: 'Same currency as the file, then run the liquidation.' },
      { label: 'Read the taxes', detail: 'The article detail lists each tax.' },
      {
        label: 'Export the detail note',
        detail: 'PDF columns follow the same order as the screen.',
      },
    ],
    relatedLinks: [
      { label: 'Files', path: '/customs/dossiers' },
      { label: 'Simulator', path: '/customs/simulation' },
    ],
  },

  '/customs/dossiers': {
    title: 'Customs files',
    summary: 'Each file sets the currency of the amounts and the rate used for XOF.',
    steps: [
      { label: 'New file', detail: 'Enter the importer, then the FOB currency.' },
      {
        label: 'Rate toward XOF',
        detail:
          'Required when the currency is not XOF. For XOF, the field is hidden and the rate is 1.',
      },
      {
        label: 'Open the file',
        detail: 'Articles and the detail note reuse this currency and rate.',
      },
    ],
    warnings: [
      'A missing or zero rate blocks saving as soon as an origin-currency amount is entered.',
    ],
  },

  '/customs/declaration': {
    title: 'Declaration',
    summary: 'File screen. Amounts, articles and liquidation open from here.',
    steps: [
      {
        label: 'Check the currency',
        detail: 'It comes from file creation: {{currency}}, rate {{rate}}.',
      },
      { label: 'Enter file amounts', detail: 'Edit the file, values block.' },
      { label: 'Add articles', detail: 'FOB, freight and insurance in the same currency.' },
      { label: 'Calculate', detail: 'The liquidation feeds the detail note.' },
    ],
    lockedSteps: [
      { label: 'File closed', detail: 'Amounts and articles can no longer be edited.' },
      { label: 'Review the liquidation', detail: 'Select an article to read the taxes.' },
      {
        label: 'Export the detail note',
        detail:
          'The PDF lists FOB XOF, CIF, then DD, RS, PCS, PCC, PROMAD, DA, VAT, BIC advance, DE, PCF, Other and the total.',
      },
    ],
  },

  'customs.declaration': {
    title: 'Declaration',
    summary: 'File in {{currency}}. XOF amounts follow rate {{rate}}.',
    steps: [
      { label: 'Check the currency', detail: 'Currency {{currency}}, rate {{rate}} toward XOF.' },
      { label: 'Enter file amounts', detail: 'Open Edit file.' },
      { label: 'Add articles', detail: 'Same currency, then calculate the liquidation.' },
      {
        label: 'Export the detail note',
        detail:
          'Columns: FOB XOF, CIF, DD, RS, PCS, PCC, PROMAD, DA, VAT, BIC advance, DE, PCF, Other, total.',
      },
    ],
    lockedSteps: [
      { label: 'File closed', detail: 'Review and export only.' },
      { label: 'Read an article', detail: 'Tax detail sits beside the list.' },
      { label: 'Detail note', detail: 'The PDF uses the same columns as the liquidation.' },
    ],
  },

  'customs.declaration.values': {
    title: 'File amounts',
    summary:
      'FOB, freight and insurance are entered in {{currency}}. Shown XOF is the amount times rate {{rate}}, rounded.',
    steps: [
      { label: 'Enter the origin amount', detail: 'FOB, freight or insurance in {{currency}}.' },
      {
        label: 'Read the XOF',
        detail: 'Example: 10.50 {{currency}} × {{rate}} = {{example}} XOF.',
      },
      {
        label: 'Save',
        detail: 'The server applies the same rounding. Expected CIF follows the XOF totals.',
      },
    ],
    warnings: ['Without a rate greater than 0, an origin amount cannot be saved.'],
    faq: [
      {
        q: 'The file is in XOF',
        a: 'The rate is 1. The amount entered is already the XOF amount.',
      },
      {
        q: 'An older file has no origin amount',
        a: 'Stored XOF is kept until an origin amount is entered.',
      },
    ],
    lockedSteps: [
      { label: 'Amounts locked', detail: 'The file is validated. Values can no longer be edited.' },
    ],
  },

  'customs.declaration.values.xof': {
    title: 'File amounts',
    summary: 'The file is in XOF. The rate is 1: the amount entered is the file amount.',
    steps: [
      { label: 'Enter FOB, freight and insurance', detail: 'The XOF fields show the same number.' },
      { label: 'Save', detail: 'No exchange rate is required.' },
    ],
    faq: [{ q: 'Why is the rate hidden?', a: 'For XOF it is hidden and forced to 1.' }],
    lockedSteps: [
      { label: 'Amounts locked', detail: 'The file is validated. Values can no longer be edited.' },
    ],
  },

  'customs.declaration.article': {
    title: 'Article',
    summary:
      'Article FOB, freight and insurance use the file currency ({{currency}}), converted to XOF at rate {{rate}}.',
    steps: [
      {
        label: 'Enter the three amounts',
        detail: 'In {{currency}}. The XOF beside them is read-only.',
      },
      { label: 'Check the example', detail: '10.50 {{currency}} × {{rate}} = {{example}} XOF.' },
      { label: 'Calculate', detail: 'Liquidation uses the XOF amounts.' },
      { label: 'Save', detail: 'The server rewrites XOF with the file rate.' },
    ],
    warnings: ['Changing the file rate means the articles must be recalculated.'],
    faq: [
      {
        q: 'The article only has XOF amounts',
        a: 'It reopens as stored. XOF is not overwritten until an origin-currency amount is entered.',
      },
      {
        q: 'The file rate is missing',
        a: 'The example uses 600. Saving an origin amount is rejected until a rate is set.',
      },
    ],
    lockedSteps: [
      {
        label: 'Article is read-only',
        detail: 'The file is closed. Open the liquidation to read the taxes.',
      },
    ],
  },

  'customs.declaration.article.xof': {
    title: 'Article',
    summary: 'The file is in XOF. FOB, freight and insurance are entered directly in XOF.',
    steps: [
      {
        label: 'Enter the three amounts',
        detail: 'The rate is 1, so the XOF amount is the same number.',
      },
      {
        label: 'Calculate, then save',
        detail: 'Liquidation and the detail note start from these amounts.',
      },
    ],
    faq: [{ q: 'Where is the conversion?', a: 'There is none. The file currency is already XOF.' }],
    lockedSteps: [{ label: 'Article is read-only', detail: 'The file is closed.' }],
  },

  'customs.declaration.liquidation': {
    title: 'Article liquidation',
    summary:
      'Each tax on the article, then the duties and taxes total. The detail note repeats these amounts as columns.',
    steps: [
      { label: 'Read the row', detail: 'Code, label, rate and XOF amount.' },
      {
        label: 'Match the detail note',
        detail:
          'Column order: FOB XOF, CIF, DD, RS, PCS, PCC, PROMAD, DA, VAT, BIC advance, DE, PCF, Other, duties and taxes total.',
      },
      {
        label: 'DA and DE',
        detail: 'DA groups excise. DE is the registration duty. Anything else goes to Other.',
      },
    ],
    warnings: [
      'After a rate or amount change, the article must be recalculated before the detail note.',
    ],
    lockedSteps: [
      { label: 'Review', detail: 'The liquidation is frozen with the file.' },
      { label: 'Export', detail: 'The detail note uses the columns in the order shown here.' },
    ],
  },

  '/customs/simulation': {
    title: 'Simulator',
    summary:
      'Estimate outside a file. FOB, freight and insurance are entered directly in XOF, with no currency or rate.',
    steps: [
      { label: 'HS code and origin', detail: 'They drive the duties.' },
      {
        label: 'Amounts in XOF',
        detail: 'No conversion. For a file in another currency, use the article form.',
      },
      { label: 'Read the liquidation', detail: 'The result is not saved on a file.' },
    ],
  },

  '/customs/circuit': {
    title: 'Customs circuit',
    summary: 'Track file steps from lodging through to release.',
    steps: [
      { label: 'Find the current step', detail: 'Each file moves along the circuit.' },
      {
        label: 'Open the declaration',
        detail: 'Amounts and liquidation are handled on the file screen.',
      },
    ],
  },

  '/customs/reports': {
    title: 'Customs reports',
    summary: 'Summaries of files and liquidations already calculated.',
    steps: [
      { label: 'Pick the period', detail: 'Amounts shown are in XOF.' },
      {
        label: 'Back to the file',
        detail: 'Tax detail and the detail note live on the declaration.',
      },
    ],
  },

  // ============================================================
  // WAREHOUSE
  // ============================================================

  '/warehouse': {
    title: 'Warehouse',
    summary: 'Manage stock, locations and movements (in/out). Periodic inventories.',
    relatedLinks: [
      { label: 'Stock', path: '/warehouse/stock' },
      { label: 'Inventories', path: '/warehouse/inventory' },
    ],
  },

  '/warehouse/stock': {
    title: 'Stock',
    summary:
      'List of stocked items with quantities, locations and values. Filter by warehouse or category.',
    fields: [
      {
        name: 'Available quantity',
        definition: 'Physical stock − reserved quantities (pending out-flow).',
      },
      { name: 'WAC valuation', definition: 'Weighted Average Cost. Standard SYSCOHADA method.' },
    ],
  },

  '/warehouse/stock/move-in': {
    title: 'Stock move-in',
    summary: 'Goods receipt: creates a stock entry and feeds account 31x on the balance sheet.',
    steps: [
      {
        label: 'Item and quantity',
        detail: 'Pick the item + enter the received quantity.',
      },
      { label: 'Location', detail: 'Specify warehouse and zone (aisle/rack).' },
      {
        label: 'Unit cost',
        detail: 'For WAC valuation. Includes all acquisition costs.',
      },
    ],
  },

  '/warehouse/inventory': {
    title: 'Inventories',
    summary:
      'Periodic physical inventories. Compare theoretical vs counted stock → adjustments to post.',
    warnings: ['A validated inventory variance posts an adjustment entry to account 603x or 71x.'],
  },

  // ============================================================
  // CUSTOMERS
  // ============================================================

  '/customers': {
    title: 'Customers',
    summary:
      'Customer master data: contact info, payment terms, outstanding, booking and invoice history.',
    fields: [
      {
        name: 'Authorized outstanding',
        definition: 'Receivables ceiling before new orders are blocked.',
      },
      { name: 'Payment terms', definition: 'Negotiated terms: 0d, 30d EOM, etc.' },
    ],
  },

  '/customers/new': {
    title: 'New customer',
    summary: 'Create a customer record: legal info, tax, commercial terms.',
    steps: [
      { label: 'Identity', detail: 'Legal name, NINEA, RC.' },
      { label: 'Contacts', detail: 'Address, primary contact, billing email.' },
      { label: 'Terms', detail: 'Payment days, authorized outstanding, default currency.' },
    ],
  },

  // ============================================================
  // EXPENSES
  // ============================================================

  '/expenses': {
    title: 'Expenses',
    summary: 'Manage expense claims: employee submission, manager approval, reimbursement.',
    relatedLinks: [
      { label: 'My claims', path: '/expenses/claims' },
      { label: 'Categories', path: '/expenses/categories' },
    ],
  },

  '/expenses/claims': {
    title: 'My expense claims',
    summary: 'List of your submitted claims with their status. Filter by period and status.',
    fields: [
      { name: 'Submitted', definition: 'Awaiting manager approval.' },
      { name: 'Approved', definition: 'Approved. Awaiting reimbursement.' },
      { name: 'Reimbursed', definition: "Paid to the employee's account." },
    ],
  },

  '/expenses/claims/new': {
    title: 'New expense claim',
    summary: 'Submit an expense for reimbursement: amount, category, mandatory receipt.',
    steps: [
      { label: 'Date and amount', detail: 'Date of the expense (not submission), gross amount.' },
      { label: 'Category', detail: 'Meals, transport, lodging, supplies, etc.' },
      { label: 'Receipt', detail: 'Photo of the receipt (mandatory above 5,000 XOF).' },
      { label: 'Submit', detail: 'Auto-notification to the manager.' },
    ],
  },

  '/expenses/categories': {
    title: 'Expense categories',
    summary:
      'Configure the categories used for expense claims. Each category maps to a SYSCOHADA account.',
  },
}
